import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle, XCircle, RotateCcw, Clock, Paperclip, Wallet, X,
  Loader2, Inbox, User, CalendarDays, IndianRupee, History,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_FILTERS = [
  { key: '', label: 'All' },
  { key: 'loan', label: 'Advance' },
  { key: 'claim_sheet', label: 'Claim Sheet' },
  { key: 'reimbursement', label: 'Reimbursement' },
  { key: 'regularization', label: 'Regularization' },
  { key: 'leave', label: 'Leave' },
  { key: 'ot', label: 'OT' },
  { key: 'shift', label: 'Shift' },
  { key: 'compoff', label: 'Comp-Off' },
];

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'sent_back', label: 'Sent Back' },
];

const STATUS_CHIP = {
  pending: 'bg-amber-100 text-amber-700',
  pending_hr: 'bg-orange-100 text-orange-700',
  in_progress: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  rework: 'bg-purple-100 text-purple-700',
  sent_back: 'bg-purple-100 text-purple-700',
};

const AMOUNT_EDIT_TYPES = ['loan', 'claim_sheet', 'reimbursement'];

const fmtAmt = (v) => (v === null || v === undefined || v === '') ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const summaryText = (row) => {
  const s = row.summary || {};
  const bits = [];
  if (s.amount !== undefined && s.amount !== null) bits.push(fmtAmt(s.amount));
  if (s.days) bits.push(`${s.days} day${s.days > 1 ? 's' : ''}`);
  if (s.hours) bits.push(`${s.hours} hrs`);
  if (s.period) bits.push(s.period);
  if (s.detail) bits.push(s.detail);
  return bits.join(' · ') || '—';
};

const DetailRow = ({ label, value }) => {
  // Ensure we never try to render an object as a React child
  const safeValue = (() => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') {
      // If it's an object, stringify it safely or skip
      try {
        return JSON.stringify(value);
      } catch {
        return '—';
      }
    }
    return value;
  })();

  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-xs font-medium text-slate-800 text-right break-words">{safeValue}</span>
    </div>
  );
};

const formatKey = (key) => {
  if (!key) return '';
  const acronyms = {
    pan: 'PAN',
    uan: 'UAN',
    esi: 'ESI',
    pf: 'PF',
    ot: 'OT',
    emi: 'EMI',
    gstr: 'GSTR',
    gst: 'GST',
    id: 'ID',
    ctc: 'CTC',
    nps: 'NPS',
    lop: 'LOP',
    rlop: 'RLOP',
  };
  return key
    .split('_')
    .map(word => acronyms[word.toLowerCase()] || (word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
};

const getDisplayableFields = (item) => {
  if (!item || typeof item !== 'object') return [];
  const skipKeys = new Set([
    '_id', 'id', 'tenant_id', 'company_id', 'employee_id',
    'workflow_id', 'approval_id', 'status', 'current_level',
    'history', 'created_at', 'updated_at', 'created_by',
    'updated_by', 'is_active', 'is_deleted', 'employee_name',
    'employee_code', 'department', 'designation', 'company',
    'approver_user_ids', 'level_name'
  ]);
  
  return Object.entries(item).filter(([key, val]) => {
    if (skipKeys.has(key)) return false;
    if (val === null || val === undefined || val === '') return false;
    // Skip any objects entirely, even empty ones
    if (typeof val === 'object') return false;
    return true;
  });
};

export const ManagerApprovalInbox = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('');
  const [detail, setDetail] = useState(null);           // { row, data|null }
  const [detailLoading, setDetailLoading] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [acting, setActing] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusTab };
      if (typeFilter) params.approval_type = typeFilter;
      const res = await axios.get(`${API_URL}/manager/approvals`, { params, withCredentials: true });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) { toast.error('Failed to load approvals'); }
    setLoading(false);
  }, [statusTab, typeFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openDetail = async (row) => {
    setDetail({ row, data: null });
    setRemarks('');
    setApprovedAmount('');
    setDetailLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/manager/approvals/${row.approval_type}/${row.id}/detail`,
        {
          params: {
            employeeId: row.employee_id,
            userId: row.user_id || row.created_by || row.applied_by,
            companyId: row.company_id,
            tenantId: row.tenant_id,
          },
          withCredentials: true,
        },
      );
      setDetail({ row, data: res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load details');
    }
    setDetailLoading(false);
  };

  const act = async (row, action) => {
    if ((action === 'reject' || action === 'rework') && !remarks.trim()) {
      toast.error('Remarks are required to reject or send back');
      return;
    }
    setActing(true);
    try {
      const payload = { action, remarks };
      if (action === 'approve' && approvedAmount !== '' && AMOUNT_EDIT_TYPES.includes(row.approval_type)) {
        payload.approved_amount = parseFloat(approvedAmount);
      }
      const res = await axios.post(
        `${API_URL}/manager/approve/${row.approval_type}/${row.id}`,
        payload, { withCredentials: true },
      );
      toast.success(res.data?.message || 'Action recorded');
      setDetail(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
    setActing(false);
  };

  const ctx = detail?.data?.context || {};
  const dRow = detail?.row;
  const dItem = detail?.data?.item || {};
  const isPendingRow = dRow && ['pending', 'pending_hr', 'in_progress'].includes(dRow.status);

  return (
    <div data-testid="manager-approval-inbox" className="space-y-4">
      {/* Status tabs + type filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
          {STATUS_TABS.map((t) => (
            <button key={t.key} data-testid={`inbox-status-${t.key}`}
              onClick={() => setStatusTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                statusTab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TYPE_FILTERS.map((t) => (
            <button key={t.key} data-testid={`inbox-type-${t.key || 'all'}`}
              onClick={() => setTypeFilter(t.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
                typeFilter === t.key
                  ? 'bg-[#0052FF] text-white border-[#0052FF]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-testid="inbox-empty">
            <Inbox className="w-8 h-8 mb-2" />
            <p className="text-sm">No {statusTab.replace('_', ' ')} requests</p>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Employee</th>
                <th className="px-4 py-2.5">Department</th>
                <th className="px-4 py-2.5">Request Date</th>
                <th className="px-4 py-2.5">Amount / Days / Period</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Pending With</th>
                <th className="px-4 py-2.5">Ageing</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.approval_type}-${row.id}`}
                  data-testid={`inbox-row-${row.approval_type}-${row.id}`}
                  className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 cursor-pointer"
                  onClick={() => openDetail(row)}>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold text-slate-800">{row.approval_label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-800">{row.employee_name || '—'}</p>
                    <p className="text-[11px] text-slate-400 font-mono">{row.employee_code}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{row.department || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {(row.request_date || row.created_at || '').slice(0, 10)}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-700">{summaryText(row)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${STATUS_CHIP[row.status] || 'bg-slate-100 text-slate-600'}`}>
                      {row.display_status || row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{row.pending_with || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs ${row.ageing_days >= 7 ? 'text-red-600 font-semibold' : row.ageing_days >= 3 ? 'text-amber-600' : 'text-slate-500'}`}>
                      <Clock className="w-3 h-3" />{row.ageing_days ?? 0}d
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`inbox-review-${row.id}`}
                      onClick={(e) => { e.stopPropagation(); openDetail(row); }}
                      className="px-3 py-1 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700">
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setDetail(null)}>
          <div data-testid="approval-detail-drawer"
            className="w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-sm font-bold text-slate-900">{dRow.approval_label}</h3>
                <p className="text-xs text-slate-500">{dRow.employee_name} · {dRow.employee_code}</p>
              </div>
              <button data-testid="detail-close" onClick={() => setDetail(null)}
                className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 space-y-5">
              {detailLoading ? (
                <div className="flex items-center justify-center py-10 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading details…
                </div>
              ) : (
                <>
                  {/* Employee & Request Summary */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">Request Information</p>
                    <DetailRow label="Employee Name" value={dRow.employee_name || dItem.employee_name || '—'} />
                    <DetailRow label="Employee Code" value={dRow.employee_code || dItem.employee_code || '—'} />
                    <DetailRow label="Department" value={dRow.department || dItem.department || '—'} />
                    {(dItem.designation || dItem.employee_designation) && (
                      <DetailRow label="Designation" value={dItem.designation || dItem.employee_designation} />
                    )}
                    {dItem.reporting_manager && (
                      <DetailRow label="Reporting Manager" value={dItem.reporting_manager} />
                    )}
                    {(dItem.company || dItem.company_name) && (
                      <DetailRow label="Company" value={dItem.company || dItem.company_name} />
                    )}
                    <DetailRow label="Request Type" value={dRow.approval_label || '—'} />
                    <DetailRow 
                      label="Submitted On" 
                      value={(() => {
                        const dt = dRow.request_date || dRow.created_at;
                        if (!dt) return '—';
                        try {
                          const d = new Date(dt);
                          return isNaN(d.getTime()) ? dt : d.toLocaleString();
                        } catch (err) {
                          return dt;
                        }
                      })()} 
                    />
                    <DetailRow label="Status" value={dRow.display_status || dRow.status} />
                    <DetailRow label="Pending With" value={dRow.pending_with} />
                    <DetailRow label="Ageing" value={`${dRow.ageing_days ?? 0} days`} />
                  </div>

                  {/* Dynamic Request Details */}
                  {getDisplayableFields(dItem).length > 0 && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-2">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">Submitted Details</p>
                      {getDisplayableFields(dItem).map(([key, val]) => {
                        const label = formatKey(key);
                        let displayVal = val;
                        
                        // 1. Attachments checking
                        const isAttachmentKey = /file|proof|attachment|bill|document|receipt|invoice/i.test(key);
                        
                        if (typeof val === 'string' && val.length === 24 && isAttachmentKey) {
                          return (
                            <div key={key} className="flex flex-col gap-1 py-1.5 border-b border-slate-100 last:border-0">
                              <span className="text-xs text-slate-500">{label}</span>
                              <a href={`${API_URL}/kyc/proofs/${val}`} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-[#0052FF] hover:underline font-semibold w-fit">
                                <Paperclip className="w-3.5 h-3.5" /> View / Download {label}
                              </a>
                            </div>
                          );
                        }
                        
                        if (Array.isArray(val)) {
                          const fileLinks = val.map((item, idx) => {
                            const fileId = typeof item === 'string' ? item : item.file_id || item.proof_file_id || item.id;
                            const fileName = item.name || item.filename || `${label} #${idx + 1}`;
                            if (fileId && typeof fileId === 'string' && fileId.length === 24) {
                              return (
                                <a key={idx} href={`${API_URL}/kyc/proofs/${fileId}`} target="_blank" rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-[#0052FF] hover:underline font-semibold bg-blue-50/50 border border-blue-100 rounded px-2 py-1 mr-2 mt-1 w-fit">
                                  <Paperclip className="w-3.5 h-3.5" /> {fileName}
                                </a>
                              );
                            }
                            return null;
                          }).filter(Boolean);
                          
                          if (fileLinks.length > 0) {
                            return (
                              <div key={key} className="flex flex-col gap-1 py-1.5 border-b border-slate-100 last:border-0">
                                <span className="text-xs text-slate-500">{label}</span>
                                <div className="flex flex-wrap">{fileLinks}</div>
                              </div>
                            );
                          }
                        }
                        
                        // 2. Formatting dates and amounts
                        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)) {
                          try {
                            const d = new Date(val);
                            if (!isNaN(d.getTime())) {
                              displayVal = d.toLocaleString();
                            }
                          } catch (e) {}
                        } else if (typeof val === 'number' && /amount|rate|emi|pay|salary|cost|total|balance|budget/i.test(key)) {
                          displayVal = fmtAmt(val);
                        } else if (typeof val === 'boolean') {
                          displayVal = val ? 'Yes' : 'No';
                        }
                        
                        // 3. Long text wrapping
                        const isLongText = /remarks|reason|description|note|comment/i.test(key) || (typeof val === 'string' && val.length > 50);
                        if (isLongText) {
                          return (
                            <div key={key} className="flex flex-col gap-1 py-2 border-b border-slate-100 last:border-0">
                              <span className="text-xs text-slate-500">{label}</span>
                              <span className="text-xs text-slate-700 break-words whitespace-pre-wrap bg-white rounded border border-slate-200 p-2.5 leading-relaxed shadow-sm">
                                {String(displayVal)}
                              </span>
                            </div>
                          );
                        }
                        
                        return (
                          <DetailRow key={key} label={label} value={displayVal} />
                        );
                      })}
                    </div>
                  )}

                  {/* ADVANCE / LOAN */}
                  {dRow.approval_type === 'loan' && (
                    <div className="space-y-3" data-testid="detail-advance-balance">
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                        {(dItem.loan_type === 'salary_advance' || dItem.loan_type === 'advance' || dItem.loan_type === 'expense_advance') ? (
                          <>
                            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Advance Request Details</p>
                            <DetailRow label="Requested Amount" value={fmtAmt(dItem.amount)} />
                            <DetailRow label="Advance Type" value={(dItem.advance_type || dItem.loan_type || '').replace(/_/g, ' ')} />
                            <DetailRow label="Advance Category" value={(dItem.advance_category || dItem.category || dItem.loan_type || '').replace(/_/g, ' ')} />
                            <DetailRow label="Purpose" value={dItem.purpose || dItem.reason || '—'} />
                            <DetailRow label="Required Date" value={dItem.required_date || dItem.start_month || '—'} />
                            <DetailRow label="Recovery Method" value={dItem.recovery_method || '—'} />
                            <DetailRow label="Recovery Start Month" value={dItem.recovery_start_month || dItem.start_month || '—'} />
                            <DetailRow label="Number of Installments" value={dItem.installment_count || dItem.tenure_months || '—'} />
                            <DetailRow label="Outstanding Advance Balance" value={fmtAmt(dItem.outstanding_advance || ctx.advance_balance?.net_balance)} />
                            <DetailRow label="Previous Pending Advances" value={ctx.previous_pending_advances ?? 0} />
                            <DetailRow label="Employee Remarks" value={dItem.remarks || dItem.reason || '—'} />
                            {dItem.supporting_document_url && (
                              <DetailRow label="Supporting Documents" value={<a href={dItem.supporting_document_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View Attachment</a>} />
                            )}
                            {dItem.attachments && Array.isArray(dItem.attachments) && dItem.attachments.length > 0 && (
                              <div className="mt-2">
                                <p className="text-[11px] text-slate-500 font-semibold mb-1">Attachments:</p>
                                {dItem.attachments.map((att, idx) => (
                                  <a key={idx} href={att.url || att} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[11px] block">{att.name || 'Attachment'}</a>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Loan Request Details</p>
                            <DetailRow label="Loan Amount" value={fmtAmt(dItem.loan_amount || dItem.amount)} />
                            <DetailRow label="Loan Type" value={(dItem.loan_type || '').replace(/_/g, ' ')} />
                            <DetailRow label="Loan Purpose" value={dItem.loan_purpose || dItem.reason || '—'} />
                            <DetailRow label="Repayment Months" value={dItem.repayment_months ? `${dItem.repayment_months} months` : (dItem.tenure_months ? `${dItem.tenure_months} months` : '—')} />
                            <DetailRow label="EMI" value={fmtAmt(dItem.emi || dItem.emi_amount)} />
                            <DetailRow label="Start Month" value={dItem.start_month || '—'} />
                            <DetailRow label="Existing Loan Balance" value={fmtAmt(ctx.advance_balance?.net_balance || dItem.existing_loan_balance)} />
                            <DetailRow label="Remarks" value={dItem.remarks || dItem.reason || '—'} />
                            {dItem.supporting_document_url && (
                              <DetailRow label="Attachment" value={<a href={dItem.supporting_document_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View Attachment</a>} />
                            )}
                            {dItem.attachments && Array.isArray(dItem.attachments) && dItem.attachments.length > 0 && (
                              <div className="mt-2">
                                <p className="text-[11px] text-slate-500 font-semibold mb-1">Attachments:</p>
                                {dItem.attachments.map((att, idx) => (
                                  <a key={idx} href={att.url || att} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[11px] block">{att.name || 'Attachment'}</a>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* CLAIM SHEET — line details + attachments + advance ref */}
                  {dRow.approval_type === 'claim_sheet' && (
                    <div className="space-y-3" data-testid="detail-claim-lines">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Claim Lines</p>
                      {(ctx.lines || []).map((ln, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 p-3 bg-white space-y-1.5 shadow-sm">
                          <div className="flex justify-between items-start border-b border-slate-100 pb-1.5 mb-1.5">
                            <div>
                              <p className="text-xs font-semibold text-slate-800">{ln.head_code}{ln.subcategory ? ` · ${ln.subcategory}` : ''}</p>
                              <p className="text-[10px] text-slate-500">Expense Date: {ln.bill_date}</p>
                            </div>
                            <span className="text-xs font-bold text-slate-900">{fmtAmt(ln.claim_amount)}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600">
                            {ln.bill_number && <div><span className="text-slate-400">Bill Number:</span> {ln.bill_number}</div>}
                            {ln.vendor && <div><span className="text-slate-400">Vendor:</span> {ln.vendor}</div>}
                            {ln.gst && <div><span className="text-slate-400">GST:</span> {ln.gst}</div>}
                            {ln.cost_center && <div><span className="text-slate-400">Cost Center:</span> {ln.cost_center}</div>}
                            {ln.project && <div><span className="text-slate-400">Project:</span> {ln.project}</div>}
                            {ln.payment_mode && <div><span className="text-slate-400">Payment Mode:</span> {ln.payment_mode}</div>}
                          </div>
                          {ln.description && (
                            <p className="text-[11px] text-slate-500 bg-slate-50 p-1.5 rounded mt-1.5">
                              <span className="text-slate-400 font-semibold">Description:</span> {ln.description}
                            </p>
                          )}
                          {ln.proof_file_id && (
                            <div className="pt-1.5 border-t border-slate-100 mt-1.5">
                              <a href={`${API_URL}/kyc/proofs/${ln.proof_file_id}`} target="_blank" rel="noreferrer"
                                data-testid={`claim-attachment-${i}`}
                                className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                                <Paperclip className="w-3 h-3" /> View Bill Attachment
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <DetailRow label="Trip" value={[dItem.trip_from, dItem.trip_to].filter(Boolean).join(' → ') || '—'} />
                        <DetailRow label="Travel Dates" value={[dItem.trip_start_date, dItem.trip_end_date].filter(Boolean).join(' → ') || '—'} />
                        <DetailRow label="Total Claim" value={fmtAmt(dItem.total_amount)} />
                        {ctx.advance_reference && (
                          <DetailRow label="Advance Reference"
                            value={`${ctx.advance_reference.loan_number || ''} — ${fmtAmt(ctx.advance_reference.principal_amount)}`} />
                        )}
                      </div>
                    </div>
                  )}

                  {/* REIMBURSEMENT — single claim */}
                  {dRow.approval_type === 'reimbursement' && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1">
                      <DetailRow label="Category" value={dItem.category} />
                      <DetailRow label="Amount" value={fmtAmt(dItem.amount)} />
                      <DetailRow label="Bill Date" value={dItem.bill_date} />
                      {dItem.bill_number && <DetailRow label="Bill Number" value={dItem.bill_number} />}
                      {dItem.vendor && <DetailRow label="Vendor" value={dItem.vendor} />}
                      {dItem.gst && <DetailRow label="GST" value={dItem.gst} />}
                      {dItem.cost_center && <DetailRow label="Cost Center" value={dItem.cost_center} />}
                      {dItem.project && <DetailRow label="Project" value={dItem.project} />}
                      {dItem.payment_mode && <DetailRow label="Payment Mode" value={dItem.payment_mode} />}
                      {dItem.description && <DetailRow label="Description" value={dItem.description} />}
                      {dItem.proof_file_id && (
                        <div className="pt-2">
                          <a href={`${API_URL}/kyc/proofs/${dItem.proof_file_id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                            <Paperclip className="w-3 h-3" /> View Attachment
                          </a>
                        </div>
                      )}
                      {dItem.policy_check?.policy_note && (
                        <p className="text-[11px] text-amber-700 mt-2 bg-amber-50 p-2 rounded border border-amber-100">{dItem.policy_check.policy_note}</p>
                      )}
                    </div>
                  )}

                  {/* REGULARIZATION — requested vs existing attendance */}
                  {dRow.approval_type === 'regularization' && (
                    <div className="space-y-3" data-testid="detail-regularization">
                      <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-blue-700 mb-2">Requested Change</p>
                        <DetailRow label="Attendance Date" value={dItem.attendance_date} />
                        <DetailRow label="Type" value={(dItem.request_type || '').replace(/_/g, ' ')} />
                        <DetailRow label="Check-In" value={dItem.proposed_in_time || '—'} />
                        <DetailRow label="Check-Out" value={dItem.proposed_out_time || '—'} />
                        <DetailRow label="Shift" value={dItem.shift || dItem.shift_name || '—'} />
                        <DetailRow label="Alternate Status" value={dItem.alternate_status || '—'} />
                        <DetailRow label="Reason" value={dItem.reason || '—'} />
                        <DetailRow label="Employee Remarks" value={dItem.employee_remarks || dItem.reason || '—'} />
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Existing Attendance</p>
                        <DetailRow label="Current Status" value={ctx.existing_attendance?.attendance_status || ctx.existing_attendance?.status || 'No record'} />
                        <DetailRow label="In Time" value={ctx.existing_attendance?.in_time || '—'} />
                        <DetailRow label="Out Time" value={ctx.existing_attendance?.out_time || '—'} />
                      </div>
                    </div>
                  )}

                  {/* LEAVE — balance context */}
                  {dRow.approval_type === 'leave' && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1" data-testid="detail-leave">
                      <DetailRow label="Leave Type" value={dItem.leave_type || dItem.leave_type_name || '—'} />
                      <DetailRow label="From Date" value={dItem.from_date || '—'} />
                      <DetailRow label="To Date" value={dItem.to_date || '—'} />
                      <DetailRow label="Number of Days" value={dItem.days || dItem.total_days || '—'} />
                      <DetailRow label="Reason" value={dItem.reason || '—'} />
                      {(dItem.supporting_document_url || dItem.medical_certificate_url || dItem.proof_file_id) && (
                        <div className="pt-2 border-t border-slate-200 mt-2">
                          {dItem.supporting_document_url && (
                            <a href={dItem.supporting_document_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline mr-4">
                              <Paperclip className="w-3 h-3" /> Supporting Doc
                            </a>
                          )}
                          {dItem.medical_certificate_url && (
                            <a href={dItem.medical_certificate_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                              <Paperclip className="w-3 h-3" /> Medical Certificate
                            </a>
                          )}
                        </div>
                      )}
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mt-4 mb-2">Leave Balances</p>
                      {(ctx.leave_balances || []).map((lb, i) => (
                        <DetailRow key={i} label={`${lb.leave_type_code || lb.leave_type_name || 'Balance'}`} value={lb.closing_balance ?? lb.balance ?? 0} />
                      ))}
                    </div>
                  )}

                  {/* OT */}
                  {dRow.approval_type === 'ot' && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1" data-testid="detail-ot">
                      <DetailRow label="OT Date" value={dItem.ot_date || dItem.date || '—'} />
                      <DetailRow label="Hours" value={dItem.hours ?? '—'} />
                      {dItem.shift && <DetailRow label="Shift" value={dItem.shift} />}
                      <DetailRow label="Reason" value={dItem.reason || '—'} />
                    </div>
                  )}

                  {/* SHIFT CHANGE */}
                  {dRow.approval_type === 'shift' && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1" data-testid="detail-shift">
                      <DetailRow label="Current Shift" value={dItem.current_shift || '—'} />
                      <DetailRow label="Requested Shift" value={dItem.requested_shift || '—'} />
                      <DetailRow label="Effective Date" value={dItem.effective_date || dItem.requested_date || dItem.from_date || '—'} />
                      <DetailRow label="Reason" value={dItem.reason || '—'} />
                    </div>
                  )}

                  {/* COMP-OFF */}
                  {dRow.approval_type === 'compoff' && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1" data-testid="detail-compoff">
                      <DetailRow label="Worked Date" value={dItem.worked_date || '—'} />
                      <DetailRow label="Eligible Date" value={dItem.eligible_date || dItem.comp_off_date || '—'} />
                      <DetailRow label="Reason" value={dItem.reason || '—'} />
                    </div>
                  )}

                  {/* Approval history / audit trail */}
                  {(detail.data?.history || []).length > 0 && (
                    <div data-testid="detail-history">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
                        <History className="w-3.5 h-3.5" /> Approval Trail
                      </p>
                      <div className="space-y-1.5">
                        {detail.data.history.map((h, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2">
                            <span className="font-semibold text-slate-700">{h.by_name || h.approver_name || h.level_name || 'System'}</span>
                            <span className="text-slate-500">{h.action || h.status}</span>
                            {(h.remarks) && <span className="text-slate-400 italic">“{h.remarks}”</span>}
                            <span className="ml-auto text-slate-400 whitespace-nowrap">{(h.at || h.decided_at || '').slice(0, 10)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action panel */}
                  {isPendingRow && (
                    <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="detail-action-panel">
                      {AMOUNT_EDIT_TYPES.includes(dRow.approval_type) && (dItem.allow_amount_edit !== false) && (
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">
                            Approved Amount (leave blank to keep {fmtAmt(dItem.total_amount ?? dItem.amount)})
                          </label>
                          <div className="relative">
                            <IndianRupee className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
                            <input type="number" min="0" data-testid="approved-amount-input"
                              value={approvedAmount}
                              onChange={(e) => setApprovedAmount(e.target.value)}
                              placeholder={`${dItem.total_amount ?? dItem.amount ?? ''}`}
                              className="w-full border-2 border-slate-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#0052FF]" />
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Remarks</label>
                        <textarea data-testid="approval-remarks-input" rows={2}
                          value={remarks} onChange={(e) => setRemarks(e.target.value)}
                          placeholder="Required for Reject / Send Back"
                          className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0052FF]" />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <button data-testid="action-approve" disabled={acting}
                          onClick={() => act(dRow, 'approve')}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                          <CheckCircle className="w-4 h-4" /> Approve
                        </button>
                        <button data-testid="action-reject" disabled={acting}
                          onClick={() => act(dRow, 'reject')}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                        <button data-testid="action-sendback" disabled={acting}
                          onClick={() => act(dRow, 'rework')}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-bold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50">
                          <RotateCcw className="w-4 h-4" /> Send Back
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerApprovalInbox;
